import React, { useEffect, useState } from 'react';
import { CommonUtil } from '../../../utils/CommonUtil';
import ImageModal from '../../../Components/ImageModal';
import { NetworkManager } from '../../../Components/NetworkManager';
import Offcanvas from '../../../Components/OffCanvas';
import InspectedImages from './InspectedImages';
import NewRelease from './NewRelease';

const SECTION_CONFIG: Record<string, { columns: string[] }> = {
  Light: { columns: ['Task Name', 'Result', 'Type', 'Count', 'Description', 'Picture'] },
  Tyre: { columns: ['Task Name', 'Status', 'Brand', 'Size', 'Depth', 'Condition', 'Description', 'Picture'] },
  Reefer: { columns: ['Task Name', 'Result', 'Present Value', 'Hour Value', 'Temperature Value', 'Picture'] },
  Picture: { columns: ['Task Name', 'Description', 'Picture'] },
  General: { columns: ['Task Name', 'Result', 'Description', 'Picture'] },
  CTPAT: { columns: ['Task Name', 'Result', 'Description'] }
};

const InspectionDetails = ({ data, getDocInspection }: any) => {
  const [selectedProcess, setSelectedProcess] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [galleryPop, setGalleryPop] = useState(false);
  const [task, setTask] = useState<any>({});
  const [imageSrcs, setImageSrcs] = useState<any[]>([]);
  const [imagesData, setImagesData] = useState<any>([]);
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [releasePopupSize, setReleasePopupSize] = useState("35%");
  
  useEffect(()=>{
    documentImages(data);
  },[])

  useEffect(() => {
    setSelectedProcess(data?.processDetails?.[0] || null);
  }, [data]);

  const onLoginError = (err: any) => {
    console.log(err);
  };

  useEffect(() => {
    if (selectedProcess) {
      const allSections = Object.keys(SECTION_CONFIG);
      const initialExpanded: Record<string, boolean> = {};
      allSections.forEach(section => {
        const hasTasks = selectedProcess.taskDetails?.some(
          (task: any) => task.taskType?.toLowerCase() === section.toLowerCase()
        );
        if (hasTasks) initialExpanded[section] = true;
      });
      setExpandedSections(initialExpanded);
    }
  }, [selectedProcess]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

 const handleImageClick = async (task: any) => {
    setTask(task);

    if (task?.taskImages && task.taskImages.length > 0) {
      setGalleryPop(true);

    const downloadPromises = task.taskImages.map((imgPath: string) => {
      const qParam = `objectPath=${imgPath}`;
      return new Promise<string>((resolve) => {
        NetworkManager.sendJsonRequest('api/session/downloadUrl', '', qParam, 'GET', true, (data: any) => resolve(data || ""), () => resolve(""));
      });
    });

    const results = await Promise.all(downloadPromises);
    const validResults = results.filter(Boolean);

    const orderedImageUrls = task.taskImages.map((imgPath: string, index: number) => ({
      key: imgPath,
      original: validResults[index] || ""
    }));

    setImageSrcs(orderedImageUrls);
    }
  };

  const renderCell = (task: any, column: string) => {
    switch (column) {
      case 'Task Name':
        return task.taskName || '—';
      case 'Type':
        return task.lightType || task.taskType || '—';
      case 'Count':
        return task.lightCount || '—';
      case 'Description':
        return (
          <div
            className="text-truncate-cell"
            title={task.description}
          >
            {task.description || '—'}
          </div>
        );
      case 'Picture':
        return (
          <span className="picture-icon" onClick={() => handleImageClick(task)}>
            {task.taskImages.length > 0 ? 
            <span role="button" className="position-relative">
              <img src="../../images/picture.svg" alt="Task" className="task-picture" />
              <span className="position-absolute top-0 mt-1 start-100 translate-middle badge rounded-pill bg-primary">
                  {task.taskImages.length}
                  <span className="visually-hidden">unread messages</span>
                </span>
              </span>
              : "—"}
          </span>
        );
      case 'Size':
        return task?.tyreSize || '—';
      case 'Brand':
        return task.tyreBrand || '—';
      case 'Depth':
        return task.tyreDepth || '—';
      case 'Condition':
        return task.tyreCondition || '—';
      case 'Status':
        return task.tyreStatus || '—';
      case 'Present Value':
        return task.presetValue || '—';
      case 'Hour Value':
        return task.hoursValue || '—';
      case 'Temperature Value':
        return task.tempratureValue || '—';
      case 'Result':
        return task.result || '—';
      default:
        return '—';
    }
  };
 
  const documentImages = (data: any) => {
    const images = {
      documentId: data?.documentId,
      docTypeName: data?.docTypeName || undefined,
      documentCode: data?.documentCode,
      vinNumber: data?.vinNumber,
      brand: data?.brand,
      unitNumber: data?.unitNumber,
      licenseNumber: data?.licenseNumber,
      saasOrgId: data?.saasOrgId,
      Images: data?.processDetails?.flatMap((process: any) =>
        process.taskDetails?.flatMap((task: any) =>
          task?.images?.map((img: any) => ({img: img?.imagePath, comment: img?.comment})) || []
        )
      )
    };
    setImagesData(images)
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className='d-flex justify-content-start mb-4'>
          <h1 className="inspection-header mb-0">{CommonUtil.getInterchangeDocumentCode()}</h1>
          <span className='mt-1 ms-0 me-3' >Date:{data?.startTime}</span>
          <span className='mx-5 mt-1'>Vin Number: {data?.vinNumber}</span>
          <div className="d-flex align-items-center mt-1 ms-5">
            <img src="../../images/download-lite.svg" alt="download" style={{width: "13px", marginTop: "3px"}} />
            <span className="mx-1" role="button" onClick={()=>{setPopupInfo({type: "ImgDownload", imageSrcs: imagesData?.Images})}} style={{textDecoration: "none", color:"#4C80CA"}}>
              <u><b>Download / Share</b></u>
            </span>{imagesData?.Images?.length} Pictures
          </div>
        </div>
        {/* <div className='d-flex justify-content-end mb-4'>
          <button className='btn-green mx-3' onClick={() => {setPopupInfo({type: "AddNewRelease"});}}>
            <i className="fa-solid fa-plus pe-1"></i>Add New Release</button>
        </div> */}
      </div>
      <div className="d-flex justify-content-start align-items-center">          
        <div className="ms-1 me-5 mb-3">
          <label className="form-label mb-0">Documents Type</label>
          <div className="d-flex align-items-center mt-2">          
            <div className="d-flex flex-wrap">
              <div className={`document-tab g-2 selected`}>{data?.docTypeName}</div>
            </div>
          </div>
        </div>
        <div>
          {data?.processDetails?.length > 0 && (        
            <div className="ms-1 mb-3">
                <label className="form-label mb-0">Process </label>
              <div className="d-flex align-items-center mt-2">             
                <div className="d-flex flex-wrap">
                  {data?.processDetails?.map((process: any) => (
                    <div
                      key={process.processId}
                      className={`process-tab ${selectedProcess?.processId === process.processId ? 'selected' : ''}`}
                      onClick={() => setSelectedProcess(process)}
                    >
                      {process.processName}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 15, marginBottom: 20 }}>
        <div className='mt-2' style={{ flex: 1, backgroundColor: '#fff', borderRadius: 5 }}>
          {selectedProcess ? (
            <>
              {Object.keys(SECTION_CONFIG).map(section => {
                const tasks = selectedProcess.taskDetails?.filter(
                  (task: any) => task.taskType?.toLowerCase() === section.toLowerCase()
                );
                if (!tasks?.length) return null;

                const { columns } = SECTION_CONFIG[section];

                return (
                  <div key={section} className="section-wrapper">
                    <div className="section-header" onClick={() => toggleSection(section)}>
                      <span className='mx-2'>{expandedSections[section] ?
                        (<i className="fa-solid fa-chevron-down"></i>) : (<i className="fa-solid fa-chevron-right"></i>)}
                      </span>
                      <h3>{section}</h3>
                    </div>
                    {expandedSections[section] && (
                    <div className="table-responsive comparison-table border border-1 ms-1">
                      <table className="table mb-0 border border-1">
                        <thead className='thead-light'>
                          <tr className="">
                            {columns?.map((col, index) => (
                              <th 
                                key={index} 
                                className={col === 'Task Name' ? 'task-name-cell' : col === 'Picture' ? "picture-name-cell" : ''}
                              >{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tasks?.map((task: any) => (
                            <tr key={task.taskId} className="">
                              {columns?.map((col, index) => (
                                <td 
                                  key={index} 
                                  className={col === 'Task Name' ? 'task-name-cell' : col === 'Picture' ? "picture-name-cell item-center" : ''}
                                  style={col === "Description" || col === 'Task Name' ? { minWidth: "100px", maxWidth: "250px", overflowWrap: "break-word" } : {}}
                                >
                                  {renderCell(task, col)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px 0', color: '#6c757d' }}>
              Select a process from the left to view details
            </div>
          )}
        </div>
      </div>
      {galleryPop && (
        <ImageModal
          isOpen={galleryPop}
          imageSrcs={imageSrcs}
          caption={task.taskName || ""}
          onClose={() => {
            setGalleryPop(false);
            setImageSrcs([]);
          }}
        />
      )}
      <Offcanvas 
        show={popupInfo?.type === "ImgDownload"}
        onHide={() => {
          setPopupInfo(null);
        }}
        title="Download / Share Pictures"
        size="70%"
      >
        <InspectedImages data={[imagesData]} imageCount={imagesData?.Images?.length} show={popupInfo?.type === "ImgDownload"} />
      </Offcanvas>
      <Offcanvas 
        show={popupInfo?.type === "AddNewRelease"}
        onHide={() => setPopupInfo(null)}
        title="Add New Release"
        size={releasePopupSize}
      >
        <NewRelease
          show={popupInfo?.type === "AddNewRelease"}
          data={data}
          onClose={() => {setPopupInfo(null); getDocInspection();}}
          popupSize={(percent: number) => setReleasePopupSize(`${percent}%`)} // callback to set size
        />
      </Offcanvas>

    </div>
  );
};

export default InspectionDetails;